(() => {
  const SELECTORS = {
    modal: '[data-size-guide-modal]',
    trigger: '[data-size-guide-trigger]',
    close: '[data-size-guide-close]',
    panel: '.size-guide-modal__panel',
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const openModals = new Set();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let previousScrollY = 0;
  let bodyLocked = false;
  let previousBodyStyles = {};
  let bodyHadModalOpen = false;
  let bodyHadOverflowHidden = false;
  let inertedElements = [];

  const getFocusableElements = (modal) =>
    Array.from(modal.querySelectorAll(focusableSelector)).filter(
      (element) => element.offsetParent !== null && element.tabIndex >= 0
    );

  const moveModalToBody = (modal) => {
    if (!modal || modal.parentElement === document.body) return;

    const section = modal.closest('[data-section]');
    if (section?.dataset.section) {
      modal.dataset.sectionId = section.dataset.section;
    }

    modal.closest('[class*="color-"]')?.classList.forEach((className) => {
      if (className.startsWith('color-')) modal.classList.add(className);
    });

    document.querySelectorAll(`${SELECTORS.modal}[id="${modal.id}"]`).forEach((existingModal) => {
      if (existingModal !== modal && existingModal.parentElement === document.body) existingModal.remove();
    });

    document.body.appendChild(modal);
  };

  const lockBodyScroll = () => {
    if (bodyLocked) return;

    previousScrollY = window.scrollY || window.pageYOffset || 0;
    previousBodyStyles = {
      left: document.body.style.left,
      paddingRight: document.body.style.paddingRight,
      position: document.body.style.position,
      right: document.body.style.right,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    bodyHadModalOpen = document.body.classList.contains('modal-open');
    bodyHadOverflowHidden = document.body.classList.contains('overflow-hidden');

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    bodyLocked = true;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${previousScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.classList.add('modal-open');
    document.body.classList.add('overflow-hidden');
    window.lenis?.stop?.();
    window.stopSmoothScroll?.();
  };

  const unlockBodyScroll = () => {
    if (!bodyLocked || openModals.size > 0) return;

    bodyLocked = false;
    if (!bodyHadModalOpen) document.body.classList.remove('modal-open');
    if (!bodyHadOverflowHidden) document.body.classList.remove('overflow-hidden');
    document.body.style.position = previousBodyStyles.position || '';
    document.body.style.top = previousBodyStyles.top || '';
    document.body.style.left = previousBodyStyles.left || '';
    document.body.style.right = previousBodyStyles.right || '';
    document.body.style.width = previousBodyStyles.width || '';
    document.body.style.paddingRight = previousBodyStyles.paddingRight || '';
    window.scrollTo(0, previousScrollY);
    if (typeof window.startSmoothScroll === 'function') {
      window.startSmoothScroll();
    } else if (!document.body.classList.contains('modal-open')) {
      window.lenis?.start?.();
    }
  };

  const setTriggersExpanded = (modal, expanded) => {
    document
      .querySelectorAll(`${SELECTORS.trigger}[aria-controls="${modal.id}"]`)
      .forEach((trigger) => trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
  };

  const getBodyChildFor = (element) => {
    let bodyChild = element;
    while (bodyChild?.parentElement && bodyChild.parentElement !== document.body) {
      bodyChild = bodyChild.parentElement;
    }

    return bodyChild;
  };

  const makeBackgroundInert = (modal) => {
    const activeBodyChild = getBodyChildFor(modal);
    inertedElements = Array.from(document.body.children).filter(
      (element) => element !== activeBodyChild && !element.inert
    );
    inertedElements.forEach((element) => {
      element.inert = true;
    });
  };

  const restoreBackground = () => {
    inertedElements.forEach((element) => {
      element.inert = false;
    });
    inertedElements = [];
  };

  const closeModal = (modal) => {
    if (!modal || modal.hidden || modal.dataset.closing === 'true') return;

    modal.dataset.closing = 'true';
    modal.classList.remove('is-open');
    modal.removeAttribute('open');
    setTriggersExpanded(modal, false);
    openModals.delete(modal);
    if (openModals.size === 0) restoreBackground();
    unlockBodyScroll();

    window.setTimeout(() => {
      modal.hidden = true;
      modal.dataset.closing = 'false';

      if (modal.openedBy && typeof modal.openedBy.focus === 'function') {
        modal.openedBy.focus({ preventScroll: true });
      }
    }, reduceMotion.matches ? 0 : 220);
  };

  const openModal = (modal, trigger) => {
    if (!modal || !trigger) return;

    moveModalToBody(modal);
    lockBodyScroll();
    modal.hidden = false;
    modal.setAttribute('open', '');
    modal.openedBy = trigger;
    openModals.add(modal);
    makeBackgroundInert(modal);
    setTriggersExpanded(modal, true);

    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      const closeButton = modal.querySelector('.size-guide-modal__close');
      closeButton?.focus({ preventScroll: true });
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(SELECTORS.trigger);
    if (trigger) {
      event.preventDefault();
      const modal =
        document.getElementById(trigger.getAttribute('aria-controls')) || document.querySelector(SELECTORS.modal);
      openModal(modal, trigger);
      return;
    }

    const closeButton = event.target.closest(SELECTORS.close);
    if (closeButton) {
      event.preventDefault();
      closeModal(closeButton.closest(SELECTORS.modal));
    }
  });

  document.addEventListener('keydown', (event) => {
    const openModalList = Array.from(openModals);
    const modal = openModalList[openModalList.length - 1];
    if (!modal) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal(modal);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements(modal);
    if (!focusableElements.length) {
      event.preventDefault();
      modal.querySelector(SELECTORS.panel)?.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    }
  });

  document.querySelectorAll(SELECTORS.modal).forEach(moveModalToBody);

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    document.querySelectorAll(`${SELECTORS.modal}[data-section-id="${sectionId}"]`).forEach((modal) => {
      openModals.delete(modal);
      modal.remove();
    });

    if (openModals.size === 0) restoreBackground();
    unlockBodyScroll();
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(SELECTORS.modal).forEach(moveModalToBody);
  });
})();
